import { createContext, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuIcon } from '@/components/primitives';
import { MiniChargingBar } from './MiniChargingBar';
import { NotificationBell } from './NotificationBell';
import { DriverThemePickerContainer } from './DriverThemePickerContainer';
import { ModeIndicatorPill } from './ModeIndicatorPill';
import { EvieLauncherButton } from './EvieLauncherButton';
import { DriverAssistSheet } from './DriverAssistSheet';
import { useEvieChatStore } from '@/store/evieChat';
import { useAuthStore } from '@/store/auth';
import { useTourStore } from '@/store/tour';
import { useActiveSession } from '@/hooks/useActiveSession';
import { TourOverlay } from '@/components/tour/TourOverlay';
import { ChargerAlertGreeter } from './ChargerAlertGreeter';
import { BirthdayCelebration } from './rewards/BirthdayCelebration';
import { TermsGate } from './TermsGate';
import { TermsPromptHost } from './TermsPromptHost';
import { PushPermissionPrompt } from './PushPermissionPrompt';
import styles from './AppShell.module.css';

interface AppShellProps {
  children: React.ReactNode;
  currentPath: string;
  onNavigate: (path: string) => void;
  /** Snapshot of the previously-rendered menu page to slide out when
   *  the user navigates from a menu route back to a fullscreen route
   *  (map / scan). Rendered as a fixed overlay over the new page. */
  exitOverlay?: ReactNode | null;
}

/**
 * Premium three-tab bottom bar. Every driver flow starts from one of these
 * three routes. Everything else (sessions, rewards, settings, etc.) is
 * reached through the Profile menu.
 */
const TABS = [
  // Animated WebP per tab (the menu icon family, EvieOrb img-swap pattern):
  // /icons/menu/{name}.webp with the -poster swap under reduced motion.
  // alt stays empty — each button already carries its visible .tabLabel, so a
  // duplicate img alt would double the accessible name.
  { id: 'find',    labelKey: 'nav.findCharger',  menuIcon: 'pin',      path: '/map'     },
  { id: 'scan',    labelKey: 'nav.startCharger', menuIcon: 'charging', path: '/scan'    },
  // Warren rename: the third tab reads "Menu" (it fronts the whole menu tree
  // — rewards, charging, money, account), route unchanged at /profile.
  { id: 'profile', labelKey: 'nav.menu',         menuIcon: 'account',  path: '/profile' },
] as const;

/**
 * True inside the exit-overlay CLONE of a departing page (AppShell renders
 * the snapshot for the 280ms slide-down). The clone is a second LIVE mount
 * — its effects run — so pages with global side effects (the EVII context
 * binding on PreSession/Charging) must check this and stand down, or the
 * clone clobbers the store the real new page just wrote.
 */
export const ExitOverlayContext = createContext(false);

// `/scan` deliberately NOT here: the scan page carries the same global
// top bar (logo / EVII orb / theme picker / bell) as every menu route.
// It was originally grouped with Map and Charging as an "immersive"
// surface, but unlike those it has no edge-to-edge canvas — the
// viewfinder is a centred 340px square on the normal page background,
// so suppressing the chrome bought nothing and cost the driver the
// bell and theme picker on a screen they sit on while charging starts.
// '/charging' (bare) is the home too — HomeRoute — see App.tsx.
const FULLSCREEN_ROUTES = new Set(['/', '/map', '/assistant', '/charging']);

export function isFullscreenRoute(path: string): boolean {
  if (FULLSCREEN_ROUTES.has(path)) return true;
  if (path.startsWith('/stations/')) return true;
  if (path.startsWith('/charging/')) return true;
  // Store is a full-bleed immersive surface: it renders its own header
  // (transparent over the hero, solid on scroll) and hides the bottom nav,
  // so the shell chrome steps aside on every /store route.
  if (path.startsWith('/store')) return true;
  if (path.startsWith('/sessions/') && path.endsWith('/complete')) return true;
  return false;
}
const isFullscreen = isFullscreenRoute;

function isTabActive(currentPath: string, tabPath: string, homeIsSession: boolean): boolean {
  // '/' is the map unless a session owns it (HomeRoute) — then it is
  // the live view and lights Start Charger, exactly as /charging/* does.
  if (tabPath === '/map') {
    return (currentPath === '/' && !homeIsSession) || currentPath === '/map' || currentPath.startsWith('/stations/');
  }
  if (tabPath === '/scan') {
    return currentPath === '/scan' || currentPath.startsWith('/charging') || (currentPath === '/' && homeIsSession);
  }
  if (tabPath === '/profile') {
    return currentPath.startsWith('/profile')
      || currentPath.startsWith('/sessions')
      || currentPath.startsWith('/history')
      || currentPath.startsWith('/billing')
      || currentPath.startsWith('/settings')
      || currentPath.startsWith('/rewards')
      || currentPath.startsWith('/leaderboard')
      || currentPath.startsWith('/favorites')
      || currentPath.startsWith('/my-chargers')
      || currentPath.startsWith('/my-fobs')
      || currentPath.startsWith('/my-access')
      || currentPath.startsWith('/my-account')
      || currentPath.startsWith('/vehicle')
      || currentPath.startsWith('/skins')
      || currentPath.startsWith('/coupons')
      || currentPath.startsWith('/wallet')
      || currentPath.startsWith('/friends')
      || currentPath.startsWith('/notifications')
      || currentPath.startsWith('/streaks')
      || currentPath.startsWith('/challenges')
      || currentPath.startsWith('/unlocks')
      || currentPath.startsWith('/shortcuts')
      || currentPath.startsWith('/help')
      || currentPath === '/home'
      || currentPath === '/dashboard';
  }
  return false;
}

export function AppShell({ children, currentPath, onNavigate, exitOverlay }: AppShellProps) {
  const { t } = useTranslation();
  const fullScreen = isFullscreen(currentPath);
  // EVII — the single sheet instance for the whole app. Mounted here,
  // OUTSIDE the keyed page wrapper below, so route changes never unmount
  // it and the active conversation (evieChat store) survives navigation.
  // Charge pages bind surface/session/charger context into the store
  // while they are mounted; everywhere else EVII runs 'general'.
  const evieOpen = useEvieChatStore((s) => s.open);
  const closeEvie = useEvieChatStore((s) => s.closeSheet);
  const evieSurface = useEvieChatStore((s) => s.surface);
  const evieSessionId = useEvieChatStore((s) => s.sessionId);
  const evieChargerId = useEvieChatStore((s) => s.chargerId);
  const evieSupportPath = useEvieChatStore((s) => s.supportPath);
  // Store is a self-contained commerce surface with its own header; the
  // primary pill nav and the mini charging bar step aside so it reads
  // full-screen (the top bar is already hidden via isFullscreenRoute).
  // The arcade joins the store here for the BOTTOM chrome only: both
  // hide the pill nav + mini charging bar. Unlike the store, /arcade
  // is NOT a fullscreen route — the global top bar stays.
  const immersiveStore = currentPath.startsWith('/store') || currentPath.startsWith('/arcade');
  // (Profile-tab unread-count dot was retired when the global
  // top bar's NotificationBell shipped — the bell's own badge
  // is the single ambient signal now. Hook + dot CSS removed.)

  // First-run guided tour: auto-open the driver-type chooser once for a
  // signed-in driver who has never run it (tour_completed_at null). Warren's
  // vision is EVII fading in centered over the map, so it is NOT route-gated.
  // Fires at most once per app session (the ref), after a short delay so a
  // boot /auth/me reconcile can settle the flag first — covering a driver who
  // completed the tour on another device. exit/finish persist completion, so
  // it never re-fires here or on any other device.
  const authUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Shared live-session cache (ambient 15s cadence). `undefined` until
  // the first fetch resolves; `null` once it has and there is nothing.
  const liveSessionQ = useActiveSession();
  const sessionKnown = liveSessionQ.data !== undefined;
  const homeIsSession = Boolean(liveSessionQ.data);
  const autoTourAttempted = useRef(false);
  useEffect(() => {
    if (autoTourAttempted.current) return;
    if (!isAuthenticated || !authUser) return;
    if (authUser.tour_completed_at) { autoTourAttempted.current = true; return; }
    // Never over a live session: the tour's steps anchor on the map's
    // quick actions, which are not on screen while the session owns the
    // home, and a driver who registered at the charger and is watching
    // it start does not want a walkthrough. Wait until the session
    // state is KNOWN (not just absent-because-unfetched). If a session
    // is running, this app open forfeits the auto-tour altogether (it
    // fires on the next open) rather than popping up wherever the
    // driver happens to be when the session ends — usually the receipt.
    if (!sessionKnown) return;
    autoTourAttempted.current = true;
    if (homeIsSession) return;
    const t = window.setTimeout(() => {
      const fresh = useAuthStore.getState().user;
      if (fresh && !fresh.tour_completed_at && !useTourStore.getState().active) {
        useTourStore.getState().open();
      }
    }, 900);
    return () => window.clearTimeout(t);
  }, [isAuthenticated, authUser, sessionKnown, homeIsSession]);

  // Track the previous path so we can pick a directional enter animation
  // when coming from a fullscreen route into a menu route.
  const prevPathRef = useRef(currentPath);
  const prevWasFullscreen = isFullscreen(prevPathRef.current);
  const isEnteringMenuFromFullscreen =
    !fullScreen && prevWasFullscreen && prevPathRef.current !== currentPath;

  useEffect(() => {
    prevPathRef.current = currentPath;
  }, [currentPath]);

  // Enter animation selection:
  //   fullscreen route     → pure crossfade (wrapper can't transform
  //                          without reparenting position:fixed kids)
  //   /arcade              → crossfade for the same reason: its room
  //                          backdrop is position:fixed, and a
  //                          transformed wrapper would become its
  //                          containing block mid-animation
  //   menu from fullscreen → slide up from bottom like a sheet
  //   menu from menu       → regular fade-up enter
  const enterClass = fullScreen || currentPath.startsWith('/arcade')
    ? styles.pageTransitionFade
    : isEnteringMenuFromFullscreen
      ? styles.pageEnterUp
      : styles.pageTransition;

  return (
    <div className={`${styles.shell} ${fullScreen ? styles.shellFullscreen : ''}`}>
      {/* Global top bar — chrome that lives on every menu route.
          Logo + wordmark on the left (lifted from ProfilePage's
          previous local brandHeader), theme picker + bell on the
          right (theme picker first, bell rightmost — founder
          preference shipped after the original bell-then-picker
          order). Hidden on FULLSCREEN_ROUTES (map / scan / charging
          / full-screen station + completion pages) so those
          immersive screens stay edge-to-edge. The bell carries the
          unread-notification badge cross-tab — the prior Profile-
          tab dot is removed in a follow-up commit since the bell
          subsumes that signal. */}
      {!fullScreen && (
        <header className={styles.topBar}>
          {/* The brand opens the map, the app's home (Warren, 23 Oct
              2026). On /arcade it stays the exit control instead: the
              arcade hides the bottom chrome, so tapping the header
              returns to the menu. */}
          {currentPath.startsWith('/arcade') ? (
            <button
              type="button"
              className={`${styles.topBarBrand} ${styles.topBarBrandButton}`}
              onClick={() => onNavigate('/profile')}
              aria-label={t('arcade:exit')}
            >
              <span aria-hidden className={styles.topBarLogo} />
              <span className={styles.topBarWord}>EV INITIATIVE</span>
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.topBarBrand} ${styles.topBarBrandButton}`}
              onClick={() => onNavigate('/map')}
              aria-label={t('common:nav.findCharger')}
            >
              <span aria-hidden className={styles.topBarLogo} />
              <span className={styles.topBarWord}>EV INITIATIVE</span>
            </button>
          )}
          <span className={styles.topBarRight}>
            {/* Fleet Mode Phase 3 — persistent mode indicator. Self-
                hides for non-fleet drivers (no fleet_id), so the
                pill never appears for the vast majority of drivers.
                Placed leftmost in topBarRight so it sits AWAY from
                the brand chrome on the left and CLOSER to the
                profile-tap surface on the right (the user reaches
                for the right side to navigate to Profile, where the
                toggle lives — the pill anchors that mental model).
                Theme picker + bell follow it; the pill's max-width
                clamps narrow viewports so the bell never wraps. */}
            <ModeIndicatorPill onNavigate={onNavigate} />
            {/* EVII everywhere: opens the global sheet over the current
                page, resuming the active conversation. Sits left of the
                theme picker so the established picker-then-bell rightmost
                pair (founder preference) stays intact. */}
            <EvieLauncherButton />
            <DriverThemePickerContainer onNavigate={onNavigate} />
            <NotificationBell onNavigate={onNavigate} />
          </span>
        </header>
      )}

      <main className={fullScreen ? styles.mainFullscreen : styles.main}>
        {/* `key={currentPath}` retriggers the enter animation on every
            route change — so tapping Profile, Scan, etc. feels like a
            transition rather than a hard swap. */}
        <div key={currentPath} className={enterClass}>
          {children}
        </div>
      </main>

      {/* Slide-down overlay: renders the previous menu page above the new
          fullscreen page and animates it off the bottom of the viewport,
          so tapping Map from Profile visually "pushes" the menu down to
          reveal the map underneath. Owned by App.tsx (snapshotted before
          the route change) and torn down after the animation completes. */}
      {exitOverlay && (
        <ExitOverlayContext.Provider value={true}>
          <div className={styles.pageExitDown} aria-hidden>
            {exitOverlay}
          </div>
        </ExitOverlayContext.Provider>
      )}

      {/* EVII chat — one instance app-wide (see the store reads above).
          BottomSheet portals to document.body, so shell layout is
          unaffected; when closed the sheet renders nothing. */}
      <DriverAssistSheet
        isOpen={evieOpen}
        onClose={closeEvie}
        surface={evieSurface}
        sessionId={evieSessionId ?? undefined}
        chargerId={evieChargerId ?? undefined}
        onNavigate={onNavigate}
        // Close first: the global sheet no longer unmounts on route
        // change, so without the explicit close the support page would
        // load underneath a still-open (and possibly listening) sheet.
        onContactSupport={() => { closeEvie(); onNavigate(evieSupportPath); }}
      />

      {/* Blocking terms-acceptance gate — one app-wide instance; portals to
          document.body above everything (incl. the tour) and renders nothing
          when the driver has already accepted the current ToS version. A
          version bump re-triggers it on next load. */}
      <TermsGate />

      {/* On-demand terms prompt — raised by an earning-enable call that hits
          409 PAYOUT_TERMS_REQUIRED, so a first-time earner accepts the payout
          terms inline and the call retries. Renders nothing when idle. */}
      <TermsPromptHost />

      {/* First-run guided tour — one app-wide instance; portals to
          document.body and renders nothing when inactive. onNavigate lets the
          tour move the app to each step's screen. */}
      <TourOverlay onNavigate={onNavigate} />

      {/* Post-install notification ask — one app-wide instance, renders
          nothing unless the driver is installed, signed in, has never
          answered, and the browser can actually grant. It is NOT an
          install prompt: see the component docblock. */}
      <PushPermissionPrompt />

      {/* Proactive charger-alert greeter — one app-wide instance. When a
          resident owner has an active alert on a home charger they own,
          EVII greets them on app open with the charger + live status,
          the reconnect guidance + nudge while offline, and a Clear once
          it is back online. Portals to body; renders nothing when there
          is no alert. Stands down while the first-run tour is active. */}
      <ChargerAlertGreeter onNavigate={onNavigate} />

      {/* Birthday celebration — one app-wide instance; portals to body and
          renders nothing until a claim or replay fires it (store-driven).
          Confetti + EVII orb + a spoken Happy-Birthday line. */}
      <BirthdayCelebration />

      {/* Persistent mini charging bar — self-hides when there's no
          active session OR when on /charging/:id (the page that
          owns the full ring). Sits 10px above the pill nav at
          z-index 49 so the pill always wins on overlap. */}
      {!immersiveStore && <MiniChargingBar currentPath={currentPath} onNavigate={onNavigate} />}

      {!immersiveStore && (
      <nav className={styles.tabBar} aria-label="Primary" data-tour="footer-nav">
        {TABS.map(({ id, labelKey, menuIcon, path }) => {
          const active = isTabActive(currentPath, path, homeIsSession);
          // Profile-only modifier — drives the Disney-12 #10
          // "exaggeration" tap response (compress → overshoot →
          // settle via a spring curve + a brief accent flare).
          // The other two tabs keep their current understated
          // press response per the spec's recommendation —
          // exaggerating all three would dilute the emphasis.
          const isProfile = id === 'profile';
          return (
            <button
              key={id}
              className={`${styles.tab} ${active ? styles.tabActive : ''} ${isProfile ? styles.tabProfile : ''}`}
              onClick={() => onNavigate(path)}
              aria-current={active ? 'page' : undefined}
              data-tour={`tab-${id}`}
            >
              <span className={styles.tabIconWrap}>
                <MenuIcon name={menuIcon} size={20} alt="" />
              </span>
              <span className={styles.tabLabel}>{t(labelKey)}</span>
            </button>
          );
        })}
      </nav>
      )}
    </div>
  );
}

